import React, { useState, useEffect } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import { FiPlus, FiMoreVertical, FiShield, FiUser, FiMail, FiCheckCircle } from 'react-icons/fi';
import { settingsService } from '../../../services/settingsService';
import { useClient } from '../../../context/ClientContext';
import supabase from '../../../supabase/supabase';
import InviteUserModal from '../../../components/settings/InviteUserModal';

const ROLE_OPTIONS = ['Super Admin', 'Accounting Admin', 'Bookkeeper', 'Member'];

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { profile } = useClient();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        // 1. Fetch current user's role securely
        if (user) {
          let { data: prof, error } = await supabase
            .from('profiles_20240320')
            .select('role, account_id')
            .eq('id', user.id)
            .single();
            
          if (error || !prof) {
            const { data: profFallback } = await supabase
              .from('profiles')
              .select('role')
              .eq('id', user.id)
              .single()
              .catch(() => ({ data: null }));
              
            if (profFallback) prof = profFallback;
          }

          if (prof?.role) {
            setCurrentUserRole(prof.role);
          } else {
            setCurrentUserRole(profile?.role || 'Super Admin'); // Default fallback for account owners
          }
        }
      } catch (err) {
        console.error("Error fetching role:", err);
        setCurrentUserRole(profile?.role || 'Super Admin');
      }

      // 2. Fetch team members
      try {
        let data = [];

        if (typeof settingsService.getUsers === 'function') {
          data = await settingsService.getUsers();
        } else {
          const { data: members, error: membersError } = await supabase.rpc('get_my_team_members');

          if (membersError) throw membersError;

          data = (members || []).map((member) => ({
            id: member.user_id,
            email: member.email || '',
            first_name: member.first_name || '',
            last_name: member.last_name || '',
            full_name: member.full_name || '',
            role: member.role || 'Member',
            account_id: member.account_id,
            created_at: member.created_at,
            status: 'Active'
          }));
        }

        setUsers(data);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err.message || 'Unable to load team members.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [profile]);

  const handleInviteSuccess = (invitedUser) => {
    // Add to local state to show it temporarily as a pending invite
    const newInvite = {
      id: `pending-${Date.now()}`,
      email: invitedUser.email,
      first_name: invitedUser.firstName,
      last_name: invitedUser.lastName,
      full_name: `${invitedUser.firstName} ${invitedUser.lastName}`,
      role: invitedUser.role,
      status: 'Pending'
    };
    setPendingInvites(prev => [...prev, newInvite]);
  };

  const handleRoleChange = async (userId, role) => {
    const previousUsers = users;
    const selectedUser = users.find(user => user.id === userId);

    if (!selectedUser || selectedUser.role === role) return;

    setSavingUserId(userId);
    setError(null);
    setSuccess(null);
    setUsers(currentUsers => currentUsers.map(user => (
      user.id === userId ? { ...user, role } : user
    )));

    try {
      const { error: updateError } = await supabase.rpc('update_team_member_role', {
        target_user_id: userId,
        new_role: role
      });

      if (updateError) throw updateError;

      setSuccess('User role updated successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setUsers(previousUsers);
      setError(err.message || 'Unable to update this user role.');
    } finally {
      setSavingUserId(null);
    }
  };

  const activeRole = currentUserRole || profile?.role || 'Bookkeeper';
  const normalizedRole = activeRole?.toLowerCase().trim();
  
  // Permission logic: Super Admin or Accounting Admin
  const canInvite = normalizedRole === 'super admin' || 
                    normalizedRole === 'accounting admin' ||
                    normalizedRole === 'client owner'; // Legacy support
  const canManageUsers = normalizedRole === 'super admin';

  const allUsers = [...users, ...pendingInvites];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-500 font-medium">Loading team members...</p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-400">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-1">Team Management</h2>
          <p className="text-sm text-slate-500">Only Super Admins and Accounting Admins can invite new members.</p>
        </div>
        <button 
          onClick={() => {
            if(canInvite) setIsInviteModalOpen(true);
          }}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm ${
            canInvite 
              ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md cursor-pointer' 
              : 'bg-slate-100 text-slate-400 cursor-not-allowed grayscale opacity-70'
          }`}
          disabled={!canInvite}
          title={!canInvite ? "You don't have permission to invite users" : "Invite a new team member"}
        >
          <SafeIcon icon={FiPlus} />
          Invite User
        </button>
      </div>

      {error && (
        <div className="mb-5 p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 text-sm font-medium">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-5 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl text-emerald-600 text-sm font-medium">
          {success}
        </div>
      )}

      <div className="bg-white border border-slate-100 rounded-[28px] overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
            <tr>
              <th className="px-6 py-5">Member</th>
              <th className="px-6 py-5">Role</th>
              <th className="px-6 py-5">Status</th>
              <th className="px-6 py-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {allUsers.length > 0 ? allUsers.map(user => {
              const isCurrentUser = user.id === profile?.id;
              const isSaving = savingUserId === user.id;

              return (
                <tr key={user.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border ${user.status === 'Pending' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                        {user.full_name?.charAt(0) || user.first_name?.charAt(0) || <SafeIcon icon={FiUser} />}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-900 text-sm">
                          {user.full_name || (user.first_name ? `${user.first_name} ${user.last_name || ''}` : 'Unnamed User')}
                          {isCurrentUser && (
                            <span className="ml-2 text-[10px] font-bold text-blue-600">(You)</span>
                          )}
                        </span>
                        <span className="text-xs text-slate-500 flex items-center gap-1.5">
                          <SafeIcon icon={FiMail} className="text-[10px]" />
                          {user.email || 'No email provided'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                      user.role?.toLowerCase() === 'super admin' ? 'bg-rose-50 text-rose-700 border-rose-100' : 
                      user.role?.toLowerCase() === 'accounting admin' ? 'bg-blue-50 text-blue-700 border-blue-100' : 
                      'bg-slate-50 text-slate-500 border-slate-100'
                    }`}>
                      {user.role || 'Bookkeeper'}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'Pending' ? 'bg-amber-400' : 'bg-emerald-500'}`}></div>
                      <span className="text-xs font-bold text-slate-700">{user.status || 'Active'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    {canManageUsers && user.status !== 'Pending' && !isCurrentUser && (
                      <select
                        value={user.role || 'Member'}
                        disabled={isSaving}
                        onChange={(event) => handleRoleChange(user.id, event.target.value)}
                        className="mr-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-600 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 disabled:opacity-50"
                        title="Manage user role"
                      >
                        {ROLE_OPTIONS.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    )}
                    {isSaving && (
                      <span className="mr-2 text-xs font-medium text-blue-600">Saving...</span>
                    )}
                    <button className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white hover:text-slate-600 hover:shadow-sm transition-all">
                      <SafeIcon icon={FiMoreVertical} />
                    </button>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center text-slate-400 text-sm italic">
                  No team members found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-10 p-6 bg-slate-900 rounded-[32px] text-white flex items-start gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-700"></div>
        <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/20 shadow-inner">
          <SafeIcon icon={FiShield} className="text-xl" />
        </div>
        <div className="relative z-10 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <h4 className="text-sm font-bold">Access Control Active</h4>
            <SafeIcon icon={FiCheckCircle} className="text-emerald-400 text-xs" />
          </div>
          <p className="text-xs text-slate-400 leading-relaxed max-w-xl">
            You are currently logged in as a <span className="text-blue-400 font-bold">{activeRole}</span>. 
            Only <span className="text-white font-medium">Super Admin</span> and <span className="text-white font-medium">Accounting Admin</span> roles have permission to manage team invitations and modify company-wide settings.
          </p>
        </div>
      </div>

      <InviteUserModal 
        isOpen={isInviteModalOpen} 
        onClose={() => setIsInviteModalOpen(false)} 
        onSuccess={handleInviteSuccess} 
      />
    </div>
  );
}